import { useEffect, useState, useCallback } from 'react'

import { Button, Dialog, Input } from '@enuygun/design-system'
import { yupResolver } from '@hookform/resolvers/yup'
import axios from 'axios'
import { useTranslation } from 'next-i18next'
import { useForm, Controller } from 'react-hook-form'
import * as Yup from 'yup'

import { useAppState } from 'context/AppContext'
import { useDeviceType } from 'hooks/useDeviceType'
import sendTrackerLog from 'utils/tracker'
import { getAnalyticsRequestId } from '../../utils/analyticsRequestId'

import { DialogInnerContainer, OptionRow, OptionText, ShowDialogButton } from './EmailManagementDialogWingie.styled'

type ReqType = 'unsub' | 'export' | 'delete'

const createRequestUrl = (market?: any, reqType?: ReqType) => {
  if (!market || !reqType) return null

  let path = ''
  if (reqType === 'unsub') path = '/email/unsubscribe-mailing/'
  else if (reqType === 'export') path = '/email/get-personal-booking-data/'
  else if (reqType === 'delete') path = '/email/remove-personal-booking-data/'

  if (market === 'us') return `https://www.wingie.com${path}`
  else if (market === 'de' || market === 'es' || market === 'ae') return `https://www.wingie.${market}${path}`
  else if (market === 'ru') return `https://${market}.wingie.com${path}`
  else if (market === 'gb') return `https://www.wingie.co.uk${path}`
  else return null
}

const EmailManagementDialogWingieElement = ({ show, setShow }: { show: boolean; setShow: (val: boolean) => void }) => {
  const { t } = useTranslation('common')
  const { market } = useAppState()
  const { isMobile, isTablet } = useDeviceType()

  const schema = Yup.object().shape({
    email: Yup.string().required(t('')),
  }) as any
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    return () => reset()
  }, [])

  const onClickButton = useCallback(
    (reqType: ReqType) => {
      handleSubmit(async (data) => {
        const environment = isMobile || isTablet ? 'MOBILE_WEB' : 'DESKTOP_WEB'
        const requestId = getAnalyticsRequestId()

        const buttonTextMap = {
          unsub: t('Footer.emailManagement.unsubBtn'),
          export: t('Footer.emailManagement.exportDataBtn'),
          delete: t('Footer.emailManagement.deleteDataBtn'),
        }

        const optionTextMap = {
          unsub: t('Footer.emailManagement.unsubText'),
          export: t('Footer.emailManagement.exportDataText'),
          delete: t('Footer.emailManagement.deleteDataText'),
        }

        // EmailManagementDialog button click tracking
        sendTrackerLog({
          category: 'wingie-homepage',
          page: 'wingie-homepage',
          type: 'click',
          message: 'email_management_dialog_button_click',
          environment: environment,
          requestId,
          requestType: reqType,
          buttonText: buttonTextMap[reqType] || null,
          optionText: optionTextMap[reqType] || null,
          linkUrl: createRequestUrl(market, reqType) || null,
          isMobile: !!(isMobile || isTablet),
          market: market,
        })

        //@ts-ignore
        await axios.post(createRequestUrl(market, reqType), data, {
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json, text/javascript', 'Access-Control-Allow-Origin': '*' },
        })
      })()
    },
    [isMobile, isTablet, market, t, handleSubmit]
  )

  return (
    <Dialog onOk={() => setShow(false)} onCancel={() => setShow(false)} title={t('Footer.emailManagement.title')} open={show} onClose={() => setShow(false)} footer={false}>
      <form onSubmit={(e) => e.preventDefault()}>
        <DialogInnerContainer>
          <Controller
            name="email"
            control={control}
            render={({ field }) => <Input status={errors.email ? 'error' : undefined} shape="default" label={t('Footer.emailManagement.email.label')} {...field} />}
          />
          <OptionRow>
            <OptionText>{t('Footer.emailManagement.unsubText')}</OptionText>
            <Button as="button" type="button" size="heightless" onClick={() => onClickButton('unsub')}>
              {t('Footer.emailManagement.unsubBtn')}
            </Button>
          </OptionRow>
          <OptionRow>
            <OptionText>{t('Footer.emailManagement.exportDataText')}</OptionText>
            <Button as="button" type="button" size="heightless" onClick={() => onClickButton('export')}>
              {t('Footer.emailManagement.exportDataBtn')}
            </Button>
          </OptionRow>
          <OptionRow>
            <OptionText>{t('Footer.emailManagement.deleteDataText')}</OptionText>
            <Button as="button" type="button" size="heightless" onClick={() => onClickButton('delete')}>
              {t('Footer.emailManagement.deleteDataBtn')}
            </Button>
          </OptionRow>
        </DialogInnerContainer>
      </form>
    </Dialog>
  )
}

const EmailManagementDialogButtonWingie = () => {
  const [show, setShow] = useState(false)
  const { t } = useTranslation('common')
  const { market } = useAppState()
  const { isMobile, isTablet } = useDeviceType()

  const handleButtonClick = useCallback(() => {
    setShow(true)

    const environment = isMobile || isTablet ? 'MOBILE_WEB' : 'DESKTOP_WEB'
    const requestId = getAnalyticsRequestId()

    // EmailManagementDialog button click tracking
    sendTrackerLog({
      category: 'wingie-homepage',
      page: 'wingie-homepage',
      type: 'click',
      message: 'email_management_dialog_open_click',
      environment: environment,
      requestId,
      buttonText: t('Footer.emailManagement.buttonText') || null,
      isMobile: !!(isMobile || isTablet),
      market: market,
    })
  }, [isMobile, isTablet, market, t])

  return (
    <>
      <ShowDialogButton onClick={handleButtonClick}>{t('Footer.emailManagement.buttonText')}</ShowDialogButton>
      <EmailManagementDialogWingieElement show={show} setShow={setShow} />
    </>
  )
}

export default EmailManagementDialogButtonWingie
